import { BusinessCriticality, Platform, ResourceType, SecurityRisk } from './common';
import { JsonHalResource } from './jsonhal.model';

export enum ResourceCategory {
  generic = 'generic',
  environment = 'environment',
  identity = 'identity',
  automation = 'automation',
  application = 'application',
  connection = 'connection',
  connector = 'connector',
  dataStorage = 'dataStorage',
  bot = 'bot',
  aiExtension = 'aiExtension',
}

export enum ResourceStatus {
  active = 'active',
  inactive = 'inactive',
  pendingDeletion = 'pendingDeletion',
}

export enum InventoryEntityFieldStatus {
  hasValue = 'hasValue',
  nullValue = 'nullValue',
  relationToUnrecognizedResource = 'relationToUnrecognizedResource',
  irrelevantForResourceCategory = 'irrelevantForResourceCategory',
  irrelevantForResourceType = 'irrelevantForResourceType',
  unsupportedForPlatformResourceType = 'unsupportedForPlatformResourceType',
  notApplicable = 'notApplicable',
}

export type InventoryAdditionalProperties = {
  [_id: string]: boolean | number | string | null;
};

export enum InventoryConnectorResourceProviderCategory {
  custom = 'custom',
  firstParty = 'firstParty',
  thirdParty = 'thirdParty',
}

export enum InventoryOrgWideAccessCategory {
  none = 'none',
  viewer = 'viewer',
  sharer = 'sharer',
  builder = 'builder',
}

export enum ResourceCreatedUsingGenerativeAi {
  yes = 'yes',
  no = 'no',
}

export enum InventoryResourcePublicationStatus {
  unpublished = 'unpublished',
  published = 'published',
}

export interface InventoryResourceState {
  status: ResourceStatus;
  statusReason?: string;
  versioningInfo: InventoryEntityField<InventoryResourceVersioningInfo>;
}

export interface InventoryResourceVersioningInfo {
  publicationStatus: InventoryResourcePublicationStatus;
  lastVersion: InventoryEntityField<InventoryResourceVersionApiModel>;
}

export interface InventoryResourceVersionApiModel {
  publishedTime: InventoryEntityField<Date>;
  version: InventoryEntityField<string>;
}

export interface InventoryEntityField<TValue> {
  value: TValue | null;
  status: InventoryEntityFieldStatus;
}

export interface InventoryPlatformUrls {
  webAppUrl: InventoryEntityField<string>;
  additionalUrls: InventoryResourceUrl[];
}

export interface InventoryResourceUrl {
  url: string;
  displayName: string;
}

export interface InventoryResourceRelationBase extends JsonHalResource {
  id: string;
  displayName: string;
}

export interface InventoryResourceEnv extends InventoryResourceRelationBase {
  environmentType: ResourceType;
}

export interface InventoryResourcePlatformInfo {
  platform: Platform;
  firstSeen: Date;
  creationTime: InventoryEntityField<Date>;
  lastModifiedTime: InventoryEntityField<Date>;
  urls: InventoryEntityField<InventoryPlatformUrls>;
  resourceId: string;
}

export interface InventoryConnectorResourceProvider {
  category: InventoryConnectorResourceProviderCategory;
  name: string;
}

export interface InventoryResourceKnownRelation extends InventoryResourceRelationBase {
  resourceCategory: ResourceCategory;
  resourceType: ResourceType;
  relationType: string;
  resourceState: InventoryResourceState;
  creationTime: InventoryEntityField<Date>;
  email: InventoryEntityField<string>;
}

export interface InventoryRelatedResourceCreator extends InventoryResourceRelationBase {
  resourceType: ResourceType;
}

export interface InventoryResourceGenericRelation extends JsonHalResource {
  relatedResourceId: string;
  relatedResourceCategory: ResourceCategory;
  relationType: string;
  additionalProperties: InventoryAdditionalProperties;
}

export interface InventoryGraphNode extends JsonHalResource {
  distinctId?: string;
  resource: InventoryGraphNodeResource;
  displayName: string;
  iconUrl?: string;
  edges?: InventoryGraphEdge[]; // Received from backend
  distinctChildrenIds?: string[]; // Assembled on the client
  children?: InventoryGraphNode[]; // Assembled on the client
  parentRelationType: string; // Populated on the client
  nodeType: InventoryGraphNodeType; // Populated on the server
}

export enum InventoryGraphNodeType {
  resource = 'resource',

  // Virtual nodes types
  orgWideAccess = 'orgWideAccess',
  publicAccess = 'publicAccess',
  m365copilot = 'm365copilot',
}

export interface InventoryGraphNodeResource extends JsonHalResource {
  id: string;
  status: ResourceStatus;
  category: ResourceCategory;
  type: ResourceType;
}

export interface InventoryGraphEdge extends JsonHalResource {
  targetResourceId: string;
  relationTypes: string[];
}

export interface InventoryResourceGraph extends JsonHalResource {
  graphNodes: InventoryGraphNode[];
}

export interface InventoryResourceIdentities {
  createdBy: InventoryEntityField<InventoryResourceKnownRelation>;
  ownedBy: InventoryEntityField<InventoryResourceKnownRelation[]>;
  lastModifiedBy: InventoryEntityField<InventoryResourceKnownRelation>;
  orgWideAccess: InventoryEntityField<InventoryOrgWideAccessCategory>;
  publicAccess: InventoryEntityField<InventoryOrgWideAccessCategory>;
  usersAccessCount: InventoryEntityField<number>;
}

export interface InventoryResourceError {
  errorCode: string;
  errorMessage: string;
}

export interface InventoryResourceErrorsInfo {
  errors: InventoryResourceError[];
}

export interface InventoryResourcePropertiesBase {}

export interface InventoryAutomationResourcePropertiesExecutionInfo {
  lastExecutionTime: InventoryEntityField<Date>;
  lastExecutionStatus: InventoryEntityField<string>;
  successfulExecutionsCount: InventoryEntityField<number>;
  failedExecutionsCount: InventoryEntityField<number>;
}

export interface InventoryAutomationResourcePropertiesSuspensionInfo {
  suspensionTime: InventoryEntityField<Date>;
  suspensionReason: InventoryEntityField<string>;
}

export interface InventoryAutomationResourceProperties extends InventoryResourcePropertiesBase {
  automationType: InventoryEntityField<string>;
  automationStatus: InventoryEntityField<string>;
  stepsCount: InventoryEntityField<number>;
  executionInfo: InventoryEntityField<InventoryAutomationResourcePropertiesExecutionInfo>;
  suspensionInfo: InventoryEntityField<InventoryAutomationResourcePropertiesSuspensionInfo>;
  usesAiModel: InventoryEntityField<boolean>;
  isSolutionAware: InventoryEntityField<boolean>;
  isBotTrigger: InventoryEntityField<boolean>;
}

export interface InventoryIdentityResourceProperties extends InventoryResourcePropertiesBase {
  identityType: InventoryEntityField<string>;
  identityProviderId: InventoryEntityField<string>;
  email: InventoryEntityField<string>;
  department: InventoryEntityField<string>;
  isActive: InventoryEntityField<boolean>;
  isExternal: InventoryEntityField<boolean>;
  isBuilder: InventoryEntityField<boolean>;
}

export interface InventoryConnectionResourceConnectorRelation extends InventoryResourceRelationBase {
  resourceType: ResourceType;
  provider: InventoryEntityField<InventoryConnectorResourceProvider>;
  resourceState: InventoryResourceState;
  creationTime: InventoryEntityField<Date>;
}

export interface InventoryConnectionResourceProperties extends InventoryResourcePropertiesBase {
  connectionStatus: InventoryEntityField<string>;
  connectionAccount: InventoryEntityField<string>;
  connector: InventoryEntityField<InventoryConnectionResourceConnectorRelation>;
  inUse: InventoryEntityField<boolean>;
  hostName: InventoryEntityField<string>;
  authenticationMethod: InventoryEntityField<string>;
  isOnPremises: InventoryEntityField<boolean>;
}

export interface InventoryConnectorResourceProperties extends InventoryResourcePropertiesBase {
  provider: InventoryEntityField<InventoryConnectorResourceProvider>;
  version: InventoryEntityField<string>;
  backendServiceUrl: InventoryEntityField<string>;
  isDeprecated: InventoryEntityField<boolean>;
}

export enum PowerPlatformApplicationQuarantineStatus {
  Quarantined = 'Quarantined',
  Unquarantined = 'Unquarantined',
}

export interface DlpInfoProperties {
  evaluationResult: InventoryEntityField<string>;
  lastEvaluationTime: InventoryEntityField<Date>;
}

export interface InventoryApplicationResourceProperties extends InventoryResourcePropertiesBase {
  applicationType: InventoryEntityField<string>;
  version: InventoryEntityField<string>;
  quarantineStatus: InventoryEntityField<string>;
  dlpEvaluationInfo: InventoryEntityField<DlpInfoProperties>;
}

export interface InventoryEnvironmentResourceProperties extends InventoryResourcePropertiesBase {
  environmentType: InventoryEntityField<string>;
}

export interface InventoryDataStorageResourceProperties extends InventoryResourcePropertiesBase {
  entityName: InventoryEntityField<string>;
  dataStorageType: InventoryEntityField<string>;
}

export interface InventoryBotResourceProperties extends InventoryResourcePropertiesBase {
  botType: InventoryEntityField<string>;
  autonomousTriggersCount: InventoryEntityField<number>;
  instructions: InventoryEntityField<string>;
  webBrowsing: InventoryEntityField<string>;
}

export interface InventoryResourceIntegration {
  id: string;
  name: string;
}

export interface InventoryResourceScores {
  businessCriticality: BusinessCriticality;
  securityRisk: SecurityRisk;
}

export interface InventoryResourceGenerativeAiProperties {
  createdUsingGenerativeAi: InventoryEntityField<ResourceCreatedUsingGenerativeAi>;
}

export class InventoryResourceModel {
  public id: string;
  public displayName: string;
  public resourceType: ResourceType;
  public resourceCategory: ResourceCategory;
  public resourceState: InventoryResourceState;
  public resourceDescription: InventoryEntityField<string>;
  public integration: InventoryResourceIntegration;
  public platformInfo: InventoryResourcePlatformInfo;
  public environment: InventoryEntityField<InventoryResourceEnv>;
  public sampleRelations: InventoryResourceGenericRelation[];
  public relatedResourcesSummary: { [category in ResourceCategory]?: number };
  public resourceCategoryProperties: InventoryResourcePropertiesBase;
  public identities: InventoryEntityField<InventoryResourceIdentities>;
  public scores?: InventoryResourceScores;
  public additionalProperties: InventoryAdditionalProperties;
  public attachments: Attachment[];
  public generativeAiProperties: InventoryEntityField<InventoryResourceGenerativeAiProperties>;
  public errorsInfo?: InventoryResourceErrorsInfo;
}

export enum AttachmentType {
  sbom = 'sbom',
}

export interface Attachment {
  id: string;
  type: AttachmentType;
  displayName: string;
  description: string;
}

export enum PowerplatformCopilotPluginType {
  pluginAction = 'pluginAction',
  topic = 'topic',
  systemTopic = 'systemTopic',
  unknown = 'N/A',
}

export enum PowerPlatformCopilotActionType {
  connectionAction = 'connectionAction',
  flowAction = 'flowAction',
}

export enum PowerPlatformCopilotActionAuthMode {
  invoker = 'invoker',
  maker = 'maker',
  flowAuth = 'flowAuthentication',
}
